import { Injectable, Signal, signal } from '@angular/core';
import { TalkJsService } from '@app/chat/domain/talk-js.service';
import { TravelDesigner } from '@app/utils/models/itinerary/travel-designer.model';
import { Observable, ReplaySubject, Subscription } from 'rxjs';
import Talk from 'talkjs';
import { map, shareReplay, tap } from 'rxjs/operators';
import { User } from '@app/utils/models/user/user.model';
import { ParticipationSettings } from 'talkjs/all';
import { TALKJS_CONFIGURATION_SETTINGS } from '@app/utils/constants/talkjs-configuration-settings';
import { SupportedLanguage } from '@app/utils/constants/supported-languages';

@Injectable({
  providedIn: 'root',
})
export class TalkJsDomainService {
  readonly unreadMessageCount$$: Signal<number>;

  private currentUnreadMessagesSubscription: Subscription | null = null;
  private currentTalkUser: Talk.User | null = null;
  private loadedPopup: Talk.Popup | null = null;
  private newConversations$: Observable<Talk.UnreadConversation[]> | null = null;
  // Bumped by destroyPopup, which every createPopup runs first: a popup still being made when it changes is no longer
  // wanted (it was closed, or a newer one replaced it), so it is destroyed rather than kept.
  private popupRequestId = 0;
  private readonly _unreadMessageCount$$ = signal(0);

  constructor(private talkJsService: TalkJsService) {
    this.unreadMessageCount$$ = this._unreadMessageCount$$.asReadonly();
  }

  public async initTalkJs(user: User): Promise<void> {
    await this.initTalkJsSession(user);
    this.newConversations$ = await this.subscribeToUnreadConversations();
  }

  public destroyTalkJsSession(): void {
    this.destroyPopup();
    this.talkJsService.destroyTalkJsSession();
    this.currentTalkUser = null;
    this.newConversations$ = null;
  }

  public destroyPopup(): void {
    this.popupRequestId++;
    if (this.loadedPopup) this.loadedPopup.destroy();
    this.loadedPopup = null;
    this.currentUnreadMessagesSubscription?.unsubscribe();
    this._unreadMessageCount$$.set(0);
  }

  // Resolves whether a popup is mounted: none is when the TalkJS session is not alive.
  public async createPopup(
    conversationId: string,
    shouldOpenChatExpanded: boolean,
    conversation: Talk.ConversationBuilder | null = null,
    theme?: Talk.ThemeOptions,
  ): Promise<boolean> {
    if (this.loadedPopup?.currentConversation?.id.includes(conversationId)) return true;
    this.destroyPopup();
    const popupRequestId = this.popupRequestId;

    const popup = await this.talkJsService.createPopup(theme);
    if (!popup) return false;

    try {
      await popup.select(conversation ?? conversationId);
      await popup.mount({ show: shouldOpenChatExpanded });
    } catch (error: unknown) {
      // A popup that failed to mount is of no use. Logging out mid-mount already destroyed it with the session, which
      // is what made TalkJS reject the pending call, so only a still alive one is destroyed here.
      if (popup.isAlive) popup.destroy();
      if (popupRequestId !== this.popupRequestId) return false;
      throw error;
    }
    // Overtaken while it was being made: only the latest popup may stay mounted.
    if (popupRequestId !== this.popupRequestId) {
      popup.destroy();
      return false;
    }

    this.loadedPopup = popup;
    this.listenCurrentUnreadMessages(conversationId);
    this.listenQuickReplyActions();
    return true;
  }

  public showPopup(): void {
    this.loadedPopup?.show();
  }

  public hidePopup(): void {
    this.loadedPopup?.hide();
  }

  // Leaves the text as a draft the member can edit before sending, with the cursor in the field.
  setMessageFieldText(text: string): void {
    this.loadedPopup?.messageField.setText(text);
    this.loadedPopup?.messageField.focus();
  }

  // Fires only for the member's own close (header cross) — not for hidePopup, destroyPopup,
  // or a popup mounted with { show: true }.
  public onPopupClose(handler: () => void): void {
    this.loadedPopup?.onClose(handler);
  }

  public configureCustomConversationAction(action: string, callback: () => void, closeChat = false): void {
    if (!this.loadedPopup) return;

    this.loadedPopup.onCustomConversationAction(action, () => {
      callback();
      if (closeChat) this.hidePopup();
    });
  }

  async createConversation(
    conversationId: string,
    title: string,
    participationSettings: Partial<ParticipationSettings> = TALKJS_CONFIGURATION_SETTINGS.defaultParticipationSettings,
    staffParticipant: TravelDesigner | null = null,
  ): Promise<Talk.ConversationBuilder | null> {
    const conversationBuilder = await this.talkJsService.getOrCreateConversation(conversationId, title);
    if (!conversationBuilder) return null;
    if (this.currentTalkUser) conversationBuilder.setParticipant(this.currentTalkUser, participationSettings);

    if (staffParticipant) {
      const staffTalkUser = await this.createTalkUser(staffParticipant, SupportedLanguage.En);
      if (staffTalkUser) conversationBuilder.setParticipant(staffTalkUser);
    }
    return conversationBuilder;
  }

  async createTalkUser(
    applicationUser: User | TravelDesigner,
    userLanguage: SupportedLanguage,
  ): Promise<Talk.User | null> {
    return await this.talkJsService.createTalkUser(applicationUser, userLanguage);
  }

  private async initTalkJsSession(user: User): Promise<void> {
    this.currentTalkUser = await this.createTalkUser(user, user.language);
    if (this.currentTalkUser) {
      await this.talkJsService.initTalkJsSession(this.currentTalkUser, user.chatSignature);
    }
  }

  private async subscribeToUnreadConversations(): Promise<Observable<Talk.UnreadConversation[]>> {
    const session = await this.talkJsService.getTalkJsSession();
    const newMessagesSubject$ = new ReplaySubject<Talk.UnreadConversation[]>(1);
    session.unreads.onChange(unreadConversations => {
      newMessagesSubject$.next(unreadConversations);
    });
    return newMessagesSubject$.asObservable().pipe(shareReplay({ bufferSize: 1, refCount: false }));
  }

  private listenCurrentUnreadMessages(conversationId: string): void {
    if (this.newConversations$) {
      this.currentUnreadMessagesSubscription = this.newConversations$
        .pipe(
          map(conversations => {
            return (
              conversations.find(({ conversation }) => conversation.id.includes(conversationId))?.unreadMessageCount ??
              0
            );
          }),
          tap(unreadMessages => this._unreadMessageCount$$.set(unreadMessages)),
        )
        .subscribe();
    }
  }

  private listenQuickReplyActions(): void {
    if (!this.loadedPopup) {
      return;
    }
    this.loadedPopup.onCustomMessageAction(TALKJS_CONFIGURATION_SETTINGS.quickReplyAction, ({ params }) => {
      const selectedAnswerText = params['choice'];
      if (selectedAnswerText) {
        this.sendMessage(selectedAnswerText);
      }
    });
  }

  private async sendMessage(messageText: string): Promise<void> {
    if (!this.loadedPopup?.currentConversation) {
      return;
    }
    const session = await this.talkJsService.getTalkJsSession();
    await session.conversation(this.loadedPopup.currentConversation.id).send(messageText);
  }
}
